import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { GradientMesh } from '@/components/ui/GradientMesh'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, EyeOff } from 'lucide-react'

export default function AuthPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { loginWithEmail } = useAppStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')


  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    const result = await loginWithEmail(email, password)
    setLoading(false)
    if (result.error) { setError(result.error.message); return }
    navigate(inviteToken ? `/invite/${inviteToken}` : '/')
  }

  return (
    <div data-auth className="min-h-dvh relative overflow-hidden" style={{ background: '#0d0d0d' }}>
      {/* Full-page gradient — visible on mobile, masked by solid left col on desktop */}
      <div className="absolute inset-0 pointer-events-none">
        <GradientMesh colors={['#010101', '#070707', '#2a2a2a']} distortion={6} swirl={0.14} speed={0.32} rotation={50} waveAmp={0.22} waveFreq={8} waveSpeed={0.11} grain={0.012} />
      </div>

      <div className="relative min-h-dvh grid lg:grid-cols-2">
      {/* Left — form */}
      <div className="flex flex-col p-8 md:p-12 lg:bg-[#0d0d0d]">
        <div className="flex items-center gap-2.5 mb-auto">
          <div className="w-7 h-7 rounded-lg bg-accent/20 flex items-center justify-center text-accent flex-shrink-0">
            <svg viewBox="0 0 16 16" fill="none" className="w-[13px] h-[13px]">
              <rect x="1" y="1" width="4" height="14" rx="2" fill="currentColor"/>
              <rect x="7" y="1" width="8" height="6" rx="2" fill="currentColor"/>
              <rect x="7" y="9" width="8" height="6" rx="2" fill="currentColor" opacity="0.38"/>
            </svg>
          </div>
          <span className="font-semibold text-sm text-text tracking-tight">PRJCT</span>
        </div>

        <div className="flex flex-1 items-center justify-center py-12">
          <div className="w-full max-w-[340px]">
            <div className="rounded-2xl p-7 lg:p-0 lg:rounded-none bg-black/75 lg:bg-transparent backdrop-blur-2xl lg:backdrop-blur-none border border-white/[0.08] lg:border-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            >
              <div className="mb-8">
                <p className="text-[52px] font-black tracking-[-0.03em] text-text leading-none">PRJCT</p>
              </div>

              <div className="mb-7">
                <h1 className="text-2xl font-bold text-text tracking-tight">Sign in</h1>
                <p className="text-sm text-text-muted mt-1">
                  {inviteToken ? "Sign in to accept your invite" : "Welcome back — enter your details below"}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Field label="Email">
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.co" className="h-10" required autoFocus />
                </Field>

                <Field
                  label="Password"
                  action={
                    <button
                      type="button"
                      className="text-xs text-text-faint hover:text-text-muted transition-colors"
                      onClick={() => {
                        if (email) useAppStore.getState().sendPasswordReset(email).then(() => setError('Password reset email sent.'))
                        else setError('Enter your email first.')
                      }}
                    >
                      Forgot password?
                    </button>
                  }
                >
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-10 pr-10" required />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-faint hover:text-text-muted transition-opacity duration-150 active:scale-[0.97]"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </Field>

                <AnimatePresence>
                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2"
                    >
                      {error}
                    </motion.p>
                  )}
                </AnimatePresence>

                <Button type="submit" variant="primary" size="lg" className="w-full mt-1" disabled={loading}>
                  {loading ? <span className="flex items-center gap-2"><Spinner /> Signing in…</span> : 'Sign in'}
                </Button>
              </form>

              <p className="text-center text-sm text-text-muted mt-6">
                Don't have an account?{' '}
                <Link
                  to={inviteToken ? `/signup?invite=${inviteToken}` : '/signup'}
                  className="text-accent hover:underline underline-offset-4 transition-colors"
                >
                  Create one
                </Link>
              </p>
            </motion.div>
            </div>
          </div>
        </div>

        <p className="text-xs text-text-faint mt-auto">© {new Date().getFullYear()} BLANQ. All rights reserved.</p>
      </div>

      {/* Right — quote only; gradient bleeds from full-page layer */}
      <div className="relative hidden lg:block overflow-hidden">
        <div className="absolute inset-0 flex flex-col justify-end p-12 pointer-events-none">
          <div style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(1px)', borderRadius: 16, padding: '24px 28px', border: '1px solid rgba(255,255,255,0.07)', maxWidth: 400 }}>
            <p className="text-white/90 text-base font-medium leading-snug" style={{ fontFamily: 'Helvetica Neue, sans-serif', letterSpacing: '-0.01em' }}>
              "Finally a project management tool that doesn't get in the way."
            </p>
            <p className="text-white/40 text-sm mt-3" style={{ fontFamily: 'Helvetica Neue, sans-serif' }}>— Early access team member</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

function Field({ label, action, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-text-muted">{label}</label>
        {action}
      </div>
      {children}
    </div>
  )
}

function Spinner() {
  return (
    <svg className="animate-spin" width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  )
}
